import {
  URL_SELECTION_ITEM_AI_GENERATE_REVIEW,
  URL_SELECTION_ITEM_PROPOSAL_CREATE,
  URL_SELECTION_ITEM_REMOVE,
  URL_SELECTION_ITEM_RESTORE,
  URL_SELECTION_ITEMS_GET,
  URL_XHR_SELECTION_ITEMS_SORT,
} from '@tools/routes/routes';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { Requests } from '@tools/requests';
import { Logger } from '@tools/logger/logger';
import type { PipedriveUser } from '@models/groups/selectionItem/pipedriveUser';
import { SelectionTab } from '@enums/selectionTab.ts';
import type { SelectionItem, UserFile } from '@models/groups/selectionItem';
import { trans } from '@elements/translation.ts';

export interface RemoveSelectionItemParams {
  selectionItemId: number;
  refusalReasons?: string[];
  refusedByUser?: boolean;
}

export const apiRemoveSelectionItem = async ({
  selectionItemId,
  refusalReasons = [],
  refusedByUser = true,
}: RemoveSelectionItemParams): Promise<boolean> => {
  try {
    await Requests.delete(URL_SELECTION_ITEM_REMOVE.replace('{id}', String(selectionItemId)), JSON.stringify({
      selectionItemId,
      refusalReasons,
      refusedByUser,
    }));
    flashBagSuccess(trans('selection_item.flashbag.archived'));
    return true;
  } catch (error) {
    flashBagError();
    Logger.error(error);
  }
  return false;
};

interface RestoreSelectionItemResponse {
  displayOrder: number;
}

export const apiRestoreSelectionItem = async (selectionItemId: number): Promise<RestoreSelectionItemResponse | null> => {
  try {
    const { data } = await Requests.post(URL_SELECTION_ITEM_RESTORE.replace('{id}', String(selectionItemId)));
    return data;
  } catch (error) {
    flashBagError();
    Logger.error(error);
  }

  return null;
};

interface CreateSelectionItemProposalParams {
  comment: string,
  promptResult?: string,
  promptGenerationCount?: number,
  attachments?: UserFile[],
}

interface CreateSelectionItemProposalResponse {
  proposalId: number,
  pipedriveUser: PipedriveUser,
  attachments?: UserFile[],
}

export const apiCreateSelectionItemProposal = async (
  selectionItemId: number,
  params: CreateSelectionItemProposalParams,
): Promise<CreateSelectionItemProposalResponse | null> => {
  try {
    const { data } = await Requests.post(
      URL_SELECTION_ITEM_PROPOSAL_CREATE.replace('{id}', String(selectionItemId)),
      params,
    );

    return data as CreateSelectionItemProposalResponse;
  } catch (error) {
    Logger.error(error);
  }

  return null;
};

interface GetSelectionItemsParams {
  status: SelectionTab,
}

export const apiGetSelectionItems = async ({ status }: GetSelectionItemsParams): Promise<SelectionItem[] | null> => {
  try {
    const { data } = await Requests.get(URL_SELECTION_ITEMS_GET, {
      params: { status },
    });

    return data as SelectionItem[];
  } catch (error) {
    flashBagError();
    Logger.error(error);
  }

  return null;
};

interface SortSelectionItemParams {
  items: {
    selectionItemId: number,
    order: number,
  }[],
}

export const apiSortSelectionItems = async ({ items }: SortSelectionItemParams): Promise<boolean> => {
  try {
    await Requests.post(URL_XHR_SELECTION_ITEMS_SORT, items);
    return true;
  } catch (e) {
    flashBagError(trans('selection_item.sort.flashbag_error'));
    Logger.error(e);
  }

  return false;
};

export interface GenerateAiReviewResponse {
  text: string,
  prompt: string,
}

export const apiGenerateAiReview = async (
  selectionItemId: number,
): Promise<GenerateAiReviewResponse | null> => {
  try {
    const { data } = await Requests.post(URL_SELECTION_ITEM_AI_GENERATE_REVIEW.replace('{id}', String(selectionItemId)));
    return data as GenerateAiReviewResponse;
  } catch (error) {
    flashBagError();
    Logger.error(error);
  }

  return null;
};
